import React, { Fragment, useRef, useState, useEffect } from "react";
import { Carousel, Icon } from "antd";
import { Document, Page } from "react-pdf/dist/esm/entry.webpack";
import {
  DownloadIcon,
  PrintIcon,
  SendAsEmailIcon,
  ZoomInIcon,
  ZoomOutIcon,
} from "./CustomIcon";
import "./DocumentPrviewCarousal.scss";

// Zoom bounds. The default view is the floor (MIN_SCALE), so Zoom Out is
// disabled at the default size and Zoom In is disabled once fully zoomed.
const MIN_SCALE = 1;
const MAX_SCALE = 2.5;
const SCALE_STEP = 0.1;

// Images upload with image_type = "normal" (not "image/...") and the carousel
// names slides by pic_code (e.g. "OTHER ATTACHMENT"), so neither field alone can
// tell image/PDF apart reliably. Sniff the real type from the original File
// object (picture_obj), data-URL MIMEs, or the src/name extension as fallbacks.
const PdfSlide = ({ item, scale }) => {
  const [numPages, setNumPages] = useState(null);
  const [error, setError] = useState(null);
  // Width/height of the fixed viewport box the PDF (or fallback iframe) lives
  // in, plus the first page's aspect ratio. Together they let us render the
  // page at an explicit pixel width that fits at scale=1 and grows with scale,
  // while the box itself stays 70vh with overflow:auto — so zooming scrolls the
  // content instead of growing the box and pushing the controls down.
  const [containerSize, setContainerSize] = useState(null);
  const [pageAspect, setPageAspect] = useState(null); // page width / height
  const containerRef = useRef(null);
  const frameWrapRef = useRef(null);

  const measureContainer = () => {
    const el = containerRef.current || frameWrapRef.current;
    if (!el || !el.clientWidth || !el.clientHeight) return;
    setContainerSize({ width: el.clientWidth, height: el.clientHeight });
  };

  useEffect(() => {
    measureContainer();
    const el = containerRef.current || frameWrapRef.current;
    if (!el || typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measureContainer);
      return () => window.removeEventListener("resize", measureContainer);
    }
    const observer = new ResizeObserver(measureContainer);
    observer.observe(el);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.src, error]);

  if (error) {
    // react-pdf fetches the file over the network and is CORS-gated, so a
    // cross-origin PDF without CORS headers can't be read by it even though an
    // iframe could embed it. Fall back to the iframe so anything that used to
    // render still renders, and keep the direct "Open" link as well. Scale the
    // iframe with a transform inside a fixed box (like the image path) instead
    // of CSS `zoom`, which would grow the 70vh element and shove the UI down.
    const scaledWidth = containerSize ? containerSize.width * scale : undefined;
    const scaledHeight = containerSize
      ? containerSize.height * scale
      : undefined;
    return (
      <Fragment>
        <div className="dpc-frame-wrap" ref={frameWrapRef}>
          <div
            className="dpc-frame-scaler"
            style={
              containerSize
                ? { width: scaledWidth, height: scaledHeight }
                : undefined
            }
          >
            <iframe
              src={item.src}
              title={item.name}
              className="dpc-frame"
              style={
                containerSize
                  ? {
                      width: containerSize.width,
                      height: containerSize.height,
                      transform: `scale(${scale})`,
                      transformOrigin: "top left",
                    }
                  : undefined
              }
            />
          </div>
        </div>
        <div className="dpc-fallback">
          <a href={item.src} target="_blank" rel="noopener noreferrer">
            Open {item.name}
          </a>
        </div>
      </Fragment>
    );
  }

  // Fit the page fully into the box at scale=1 (bounded by both width and
  // height once the aspect ratio is known), then multiply by scale to zoom.
  let basePageWidth;
  if (containerSize) {
    basePageWidth = pageAspect
      ? Math.min(containerSize.width, containerSize.height * pageAspect)
      : containerSize.width;
  }
  const pageWidth = basePageWidth ? basePageWidth * scale : undefined;

  return (
    <div className="dpc-pdf" ref={containerRef}>
      <Document
        file={item.src}
        loading={<p className="dpc-pdf-message">Loading PDF...</p>}
        onLoadSuccess={({ numPages }) => setNumPages(numPages)}
        onLoadError={setError}
      >
        {numPages != null &&
          pageWidth &&
          Array.from(new Array(numPages), (_, i) => (
            <Page
              key={i}
              pageNumber={i + 1}
              width={pageWidth}
              onLoadSuccess={
                i === 0
                  ? (page) => {
                      const w = page.originalWidth || page.width;
                      const h = page.originalHeight || page.height;
                      if (w && h) setPageAspect(w / h);
                    }
                  : undefined
              }
            />
          ))}
      </Document>
    </div>
  );
};

// Fit the image into the viewport at scale=1, then grow a real layout box by
// scale so overflow:auto gets both horizontal and vertical scroll for any
// portrait/landscape size. CSS zoom alone only expanded height reliably here.
const ImageSlide = ({ item, scale }) => {
  const wrapRef = useRef(null);
  const imgRef = useRef(null);
  const [baseSize, setBaseSize] = useState(null);

  const measureBaseSize = () => {
    const wrap = wrapRef.current;
    const img = imgRef.current;
    if (!wrap || !img || !img.naturalWidth) return;

    const maxW = wrap.clientWidth;
    const maxH = wrap.clientHeight;
    if (!maxW || !maxH) return;

    const fit = Math.min(maxW / img.naturalWidth, maxH / img.naturalHeight, 1);
    setBaseSize({
      width: img.naturalWidth * fit,
      height: img.naturalHeight * fit,
    });
  };

  useEffect(() => {
    setBaseSize(null);
    measureBaseSize();
    const wrap = wrapRef.current;
    if (!wrap || typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measureBaseSize);
      return () => window.removeEventListener("resize", measureBaseSize);
    }
    const observer = new ResizeObserver(measureBaseSize);
    observer.observe(wrap);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.src]);

  const scaledWidth = baseSize ? baseSize.width * scale : undefined;
  const scaledHeight = baseSize ? baseSize.height * scale : undefined;

  return (
    <div
      className={`dpc-image-wrap${scale > 1 ? " dpc-image-wrap--zoomed" : ""}`}
      ref={wrapRef}
    >
      <div
        className="dpc-image-scaler"
        style={
          baseSize
            ? { width: scaledWidth, height: scaledHeight }
            : undefined
        }
      >
        <img
          ref={imgRef}
          src={item.src}
          alt={item.name}
          className="dpc-image"
          onLoad={measureBaseSize}
          style={
            baseSize
              ? {
                  width: baseSize.width,
                  height: baseSize.height,
                  maxWidth: "none",
                  maxHeight: "none",
                  transform: `scale(${scale})`,
                  transformOrigin: "top left",
                }
              : undefined
          }
        />
      </div>
    </div>
  );
};

const getPreviewKind = (item) => {
  const type = item.image_type || "";
  const srcUrl = (item.src || "").split("?")[0];
  const name = item.name || "";
  const pictureObj = item.picture_obj;

  if (type.startsWith("image/")) return "image";
  if (type === "application/pdf") return "pdf";
  if (type === "signature" && srcUrl) return "image";

  if (pictureObj && typeof pictureObj.type === "string") {
    if (pictureObj.type.startsWith("image/")) return "image";
    if (pictureObj.type === "application/pdf") return "pdf";
  }

  if (srcUrl.startsWith("data:")) {
    if (/^data:image\//.test(srcUrl)) return "image";
    if (/^data:application\/pdf/.test(srcUrl)) return "pdf";
  }

  const isImageExt = /\.(jpe?g|png|gif|webp|bmp|svg)$/i;
  const pictureName = pictureObj && pictureObj.name;
  if (
    isImageExt.test(name) ||
    isImageExt.test(srcUrl) ||
    (pictureName && isImageExt.test(pictureName))
  ) {
    return "image";
  }
  if (
    /\.pdf$/i.test(name) ||
    /\.pdf$/i.test(srcUrl) ||
    (pictureName && /\.pdf$/i.test(pictureName))
  ) {
    return "pdf";
  }

  return "other";
};

/**
 * Multi-slide preview for a mix of images, PDFs, and other file types.
 *
 * Props:
 * - items: array of { src, name, image_type, ack_id, pictures_id, isSaved }
 * - startIndex: which slide to open on (defaults to 0)
 * - onPrint: callback used for the download/print actions (mirrors FilePreviewer)
 * - setIsPODemailModalVisible: callback to open the "send as email" drawer
 * - toggleSelectDocument: keeps the previewed document in sync with the slide
 * - enableSelection: whether the carousel should update the selected doc
 */
const formatCashReceiptAmount = (val) => {
  if (val === undefined || val === null || val === "") return "N/A";
  const strVal = val.toString().trim();
  const num = parseFloat(strVal.replace(/[^0-9.-]+/g, ""));
  if (isNaN(num)) return strVal;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(num);
};

export default function DocumentPreviewCarousel({
  items = [],
  startIndex = 0,
  onPrint = () => {},
  setIsPODemailModalVisible,
  toggleSelectDocument = () => {},
  enableSelection = true,
  showActions = true,
}) {
  const carouselRef = useRef(null);
  const [current, setCurrent] = useState(startIndex);
  const [scale, setScale] = useState(MIN_SCALE);

  // Jump to the right slide whenever the caller opens a different starting item
  useEffect(() => {
    if (carouselRef.current && typeof carouselRef.current.goTo === "function") {
      carouselRef.current.goTo(startIndex, true);
      setCurrent(startIndex);
      setScale(MIN_SCALE);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startIndex]);

  if (items.length === 0) return null;

  const currentItem = items[current] || items[0];

  const isFirst = current === 0;
  const isLast = current === items.length - 1;

  const goPrev = () => {
    if (isFirst) return;
    carouselRef.current?.prev();
  };
  const goNext = () => {
    if (isLast) return;
    carouselRef.current?.next();
  };

  const handleZoomIn = () =>
    setScale((prevScale) => Math.min(prevScale + SCALE_STEP, MAX_SCALE));
  const handleZoomOut = () =>
    setScale((prevScale) => Math.max(prevScale - SCALE_STEP, MIN_SCALE));

  const isMinScale = scale <= MIN_SCALE;
  const isMaxScale = scale >= MAX_SCALE;

  // Keep the "selected/previewed" document in sync so download, email and
  // print always act on the slide currently being viewed. Reset zoom to the
  // default so each slide opens at its default (Zoom Out disabled) size.
  const handleSlideChange = (index) => {
    setCurrent(index);
    setScale(MIN_SCALE);
    const item = items[index];
    if (item && enableSelection && item.ack_id) {
      toggleSelectDocument(item.ack_id, item.pictures_id, true);
    }
  };

  const renderActionContainer = () => (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        gap: "8px",
        textAlign: "center",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          width: "360px",
          height: "56px",
          borderRadius: "64px",
          gap: "24px",
          boxShadow: "0px 12px 24px 0px rgba(0, 0, 0, 0.28)",
        }}
      >
        <div
          style={{
            display: "flex",
            gap: "16px",
            padding: "8px",
          }}
        >
          <ZoomOutIcon
            color={isMinScale ? "#B0B8C4" : undefined}
            style={{
              cursor: isMinScale ? "not-allowed" : "pointer",
              opacity: isMinScale ? 0.5 : 1,
            }}
            onClick={isMinScale ? undefined : handleZoomOut}
          />
          <ZoomInIcon
            color={isMaxScale ? "#B0B8C4" : undefined}
            style={{
              cursor: isMaxScale ? "not-allowed" : "pointer",
              opacity: isMaxScale ? 0.5 : 1,
            }}
            onClick={isMaxScale ? undefined : handleZoomIn}
          />
        </div>
        {currentItem?.isSaved && (
          <div
            style={{
              display: "flex",
              gap: "16px",
              padding: "8px",
            }}
          >
            <DownloadIcon
              color="#5064AE"
              style={{ cursor: "pointer" }}
              onClick={() => onPrint(true)}
            />
            <SendAsEmailIcon
              style={{ cursor: "pointer" }}
              onClick={() => {
                setIsPODemailModalVisible &&
                  setIsPODemailModalVisible(true);
              }}
            />
            <PrintIcon
              color="#5064AE"
              style={{ cursor: "pointer" }}
              onClick={() => onPrint()}
            />
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="dpc-wrap">
      {items.length > 1 && (
        <button
          type="button"
          className={`dpc-arrow dpc-arrow--left${isFirst ? " dpc-arrow--disabled" : ""}`}
          onClick={goPrev}
          disabled={isFirst}
          aria-label="Previous"
        >
          <Icon type="left" />
        </button>
      )}

      <Carousel
        ref={carouselRef}
        dots={items.length > 1}
        infinite={false}
        afterChange={handleSlideChange}
        keyboard={false}
      >
        {items.map((item, index) => {
          const kind = getPreviewKind(item);
          const isCashReceipt =
            (item.pic_code || "").toUpperCase() === "CASH RECEIPT" ||
            (item.pic_title || "").toUpperCase() === "CASH RECEIPT" ||
            (item.name || "").toUpperCase() === "CASH RECEIPT";

          return (
            <div key={item.uid || item.src || index} className="dpc-slide">
              {isCashReceipt && (
                <div
                  className="dpc-cash-receipt-badge"
                  data-testid="cash-receipt-amount-badge"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "10px",
                    background: "linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)",
                    border: "1px solid #a7f3d0",
                    padding: "8px 20px",
                    borderRadius: "24px",
                    margin: "0 auto 16px auto",
                    boxShadow: "0 2px 8px rgba(16, 185, 129, 0.15)",
                  }}
                >
                  <div
                    style={{
                      width: "24px",
                      height: "24px",
                      borderRadius: "50%",
                      backgroundColor: "#10b981",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#ffffff",
                      fontSize: "14px",
                      fontWeight: "bold",
                    }}
                  >
                    $
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    <span
                      style={{
                        fontSize: "13px",
                        color: "#047857",
                        fontWeight: 600,
                        letterSpacing: "0.2px",
                      }}
                    >
                      Prepaid Amount:
                    </span>
                    <span
                      style={{
                        fontSize: "16px",
                        color: "#065f46",
                        fontWeight: 700,
                      }}
                    >
                      {formatCashReceiptAmount(item.amount)}
                    </span>
                  </div>
                </div>
              )}
              {kind === "image" && (
                <ImageSlide item={item} scale={scale} />
              )}
              {kind === "pdf" && (
                <PdfSlide item={item} scale={scale} />
              )}
              {kind === "other" && (
                <div className="file-preview-error">
                  <p className="file-preview-error-text">
                    Preview not available for this file type.
                  </p>
                  <a
                    className="file-preview-error-button"
                    href={item.src}
                    download
                  >
                    Download {item.name}
                  </a>
                </div>
              )}
              <p className="dpc-caption">{item.name}</p>
            </div>
          );
        })}
      </Carousel>

      {items.length > 1 && (
        <button
          type="button"
          className={`dpc-arrow dpc-arrow--right${isLast ? " dpc-arrow--disabled" : ""}`}
          onClick={goNext}
          disabled={isLast}
          aria-label="Next"
        >
          <Icon type="right" />
        </button>
      )}

      {items.length > 1 && (
        <div className="dpc-counter">
          {current + 1} / {items.length}
        </div>
      )}

      {showActions && renderActionContainer()}
    </div>
  );
}
